import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { StatCard } from "@/components/admin/StatCard";
import { ChartCard } from "@/components/stats/ChartCard";
import { Users, FileText, TrendingUp, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

type DateFilter = "all" | "year" | "month" | "week" | "today";

interface KPIData {
  totalProfessionals: number;
  totalPatients: number;
  totalDocuments: number;
  activeSubscriptions: number;
}

interface ProfessionalsByArea {
  name: string;
  total: number;
}

interface PatientDistribution {
  name: string;
  value: number;
  [key: string]: string | number;
}

interface ContentType {
  type: string;
  count: number;
}

interface AccessByArea {
  area: string;
  total: number;
}

interface RecentAccessLog {
  id: string;
  action: string;
  entity: string;
  created_at: string;
  ip: string | null;
  professional_name: string | null;
  /** Texto da coluna Área: especialidade, Paciente, Admin ou — */
  area_display: string;
}

/** Linha retornada pelo select de audit_logs com join em profiles (últimos acessos) */
type AuditRecentRow = {
  id: string;
  action: string;
  entity: string;
  created_at: string;
  ip: string | null;
  actor_id: string | null;
  profiles: { full_name: string | null; health_area: string | null } | null;
};

const COLORS = ['#0891b2', '#06b6d4', '#22d3ee', '#67e8f9', '#a5f3fc'];

const formatAreaName = (slug: string): string => {
  const map: Record<string, string> = {
    'medicina': 'Medicina',
    'nutricao': 'Nutrição',
    'fisioterapia': 'Fisioterapia',
    'educacao-fisica': 'Educação Física',
    'psicologia': 'Psicologia',
    'enfermagem': 'Enfermagem',
    'odontologia': 'Odontologia',
    'farmacia': 'Farmácia',
    'biomedicina': 'Biomedicina',
    'terapia-ocupacional': 'Terapia Ocupacional',
    'fonoaudiologia': 'Fonoaudiologia',
  };
  return map[slug] || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, ' ') : 'Não informado');
};

const formatActionName = (action: string): string => {
  const map: Record<string, string> = {
    'view_dashboard': 'Acessou dashboard',
    'view_patient': 'Visualizou paciente',
    'create_patient': 'Criou paciente',
    'update_patient': 'Atualizou paciente',
    'delete_patient_cascade': 'Deletou paciente',
    'view_patients': 'Visualizou lista de pacientes',
    'create_document': 'Criou documento',
    'view_document': 'Visualizou documento',
    'delete_document': 'Deletou documento',
    'view_statistics': 'Visualizou estatísticas',
    'create_smartfile': 'Criou smartfile',
    'view_smartfile': 'Visualizou smartfile',
    'login': 'Fez login',
    'logout': 'Fez logout',
  };
  return map[action] || action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export default function AdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [kpis, setKpis] = useState<KPIData>({ totalProfessionals: 0, totalPatients: 0, totalDocuments: 0, activeSubscriptions: 0 });
  const [professionalsByArea, setProfessionalsByArea] = useState<ProfessionalsByArea[]>([]);
  const [patientsByAge, setPatientsByAge] = useState<PatientDistribution[]>([]);
  const [patientsBySex, setPatientsBySex] = useState<PatientDistribution[]>([]);
  const [contentTypes, setContentTypes] = useState<ContentType[]>([]);
  const [accessByArea, setAccessByArea] = useState<AccessByArea[]>([]);
  const [recentAccesses, setRecentAccesses] = useState<RecentAccessLog[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, [dateFilter]);

  const getDateFilter = () => {
    const now = new Date();
    switch (dateFilter) {
      case "today":
        return now.toISOString().split('T')[0];
      case "week":
        const weekAgo = new Date(now.setDate(now.getDate() - 7));
        return weekAgo.toISOString().split('T')[0];
      case "month":
        const monthAgo = new Date(now.setMonth(now.getMonth() - 1));
        return monthAgo.toISOString().split('T')[0];
      case "year":
        const yearAgo = new Date(now.setFullYear(now.getFullYear() - 1));
        return yearAgo.toISOString().split('T')[0];
      default:
        return "1970-01-01";
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const startDate = getDateFilter();

      // KPIs
      const [professionalsRes, patientsRes, documentsRes, subscriptionsRes] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }).gte("created_at", startDate),
        supabase.from("patients_onboarding").select("id", { count: "exact", head: true }).gte("created_at", startDate),
        supabase.from("documents_onboarding").select("id", { count: "exact", head: true }).gte("created_at", startDate),
        supabase.from("subscriptions").select("id", { count: "exact", head: true }).eq("status", "active"),
      ]);

      setKpis({
        totalProfessionals: professionalsRes.count || 0,
        totalPatients: patientsRes.count || 0,
        totalDocuments: documentsRes.count || 0,
        activeSubscriptions: subscriptionsRes.count || 0,
      });

      // Profissionais por área
      const { data: areaData } = await supabase
        .from("profiles")
        .select("health_area_id, health_areas(name)")
        .gte("created_at", startDate);

      const areaCount: Record<string, number> = {};
      areaData?.forEach((p: any) => {
        const areaName = p.health_areas?.name || "Sem área";
        areaCount[areaName] = (areaCount[areaName] || 0) + 1;
      });
      setProfessionalsByArea(Object.entries(areaCount).map(([name, total]) => ({ name, total })));

      // Pacientes por faixa etária e sexo
      const { data: patientsData } = await supabase
        .from("patients_onboarding")
        .select("data_nascimento, sexo")
        .gte("created_at", startDate);

      const ageGroups: Record<string, number> = { "0-18": 0, "19-30": 0, "31-50": 0, "51-70": 0, "70+": 0 };
      const sexGroups: Record<string, number> = { Masculino: 0, Feminino: 0, Outro: 0 };

      patientsData?.forEach((p: any) => {
        if (p.data_nascimento) {
          const age = new Date().getFullYear() - new Date(p.data_nascimento).getFullYear();
          if (age <= 18) ageGroups["0-18"]++;
          else if (age <= 30) ageGroups["19-30"]++;
          else if (age <= 50) ageGroups["31-50"]++;
          else if (age <= 70) ageGroups["51-70"]++;
          else ageGroups["70+"]++;
        }
        if (p.sexo) {
          if (p.sexo === "M") sexGroups.Masculino++;
          else if (p.sexo === "F") sexGroups.Feminino++;
          else sexGroups.Outro++;
        }
      });

      setPatientsByAge(Object.entries(ageGroups).map(([name, value]) => ({ name, value })));
      setPatientsBySex(Object.entries(sexGroups).map(([name, value]) => ({ name, value })));

      // Tipos de conteúdo
      const { data: contentData } = await supabase
        .from("documents_onboarding")
        .select("content_kind")
        .gte("created_at", startDate)
        .not("content_kind", "is", null);

      const contentCount: Record<string, number> = {};
      contentData?.forEach((d: any) => {
        contentCount[d.content_kind] = (contentCount[d.content_kind] || 0) + 1;
      });
      setContentTypes(Object.entries(contentCount).map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count).slice(0, 10));

      // Acessos por área da saúde
      const { data: accessData } = await supabase
        .from("audit_logs")
        .select("actor_id, profiles!fk_audit_logs_actor(health_area)")
        .gte("created_at", startDate);

      const accessCount: Record<string, number> = {};
      accessData?.forEach((log: any) => {
        const area = log.profiles?.health_area || '';
        const formattedArea = formatAreaName(area);
        accessCount[formattedArea] = (accessCount[formattedArea] || 0) + 1;
      });
      setAccessByArea(Object.entries(accessCount).map(([area, total]) => ({ area, total })).sort((a, b) => b.total - a.total));

      // Últimos 20 acessos
      const { data: recentAccessData, error: recentAccessError } = await supabase
        .from("audit_logs")
        .select("id, action, entity, created_at, ip, actor_id, profiles!fk_audit_logs_actor(full_name, health_area)")
        .order("created_at", { ascending: false })
        .limit(20);

      if (recentAccessError) {
        console.error("Error fetching recent accesses:", recentAccessError);
      }

      const rawLogs: AuditRecentRow[] = (recentAccessData ?? []) as AuditRecentRow[];
      const needPatientCheck = new Set<string>();
      for (const log of rawLogs) {
        const area = log.profiles?.health_area ?? null;
        if (!area && log.actor_id) needPatientCheck.add(log.actor_id);
      }

      const patientActorIds = new Set<string>();
      await Promise.all(
        [...needPatientCheck].map(async (userId) => {
          const { data, error } = await supabase.rpc("has_role", {
            _user_id: userId,
            _role: "patient",
          });
          if (!error && data === true) patientActorIds.add(userId);
        }),
      );

      const formattedAccesses: RecentAccessLog[] = rawLogs.map((log) => {
        const health_area = log.profiles?.health_area || null;
        let area_display: string;
        if (health_area) {
          area_display = formatAreaName(health_area);
        } else if (!log.actor_id) {
          area_display = "—";
        } else if (patientActorIds.has(log.actor_id)) {
          area_display = "Paciente";
        } else {
          area_display = "Admin";
        }
        return {
          id: log.id,
          action: log.action,
          entity: log.entity,
          created_at: log.created_at,
          ip: log.ip,
          professional_name: log.profiles?.full_name || "Desconhecido",
          area_display,
        };
      });
      setRecentAccesses(formattedAccesses);

    } catch (error) {
      console.error("Error loading dashboard:", error);
      toast.error("Erro ao carregar dados do dashboard");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Visão geral do sistema</p>
        </div>
        <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as DateFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tudo até hoje</SelectItem>
            <SelectItem value="year">Este ano</SelectItem>
            <SelectItem value="month">Este mês</SelectItem>
            <SelectItem value="week">Últimos 7 dias</SelectItem>
            <SelectItem value="today">Hoje</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Profissionais"
          value={kpis.totalProfessionals}
          icon={Users}
          iconColor="text-primary"
        />
        <StatCard
          title="Pacientes"
          value={kpis.totalPatients}
          icon={Users}
          iconColor="text-cyan-600"
        />
        <StatCard
          title="Documentos"
          value={kpis.totalDocuments}
          icon={FileText}
          iconColor="text-blue-600"
        />
        <StatCard
          title="Assinaturas Ativas"
          value={kpis.activeSubscriptions}
          icon={TrendingUp}
          iconColor="text-green-600"
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <ChartCard title="Profissionais por Área da Saúde" description="Distribuição por especialidade">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={professionalsByArea}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="total" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Distribuição de Pacientes por Idade" description="Faixas etárias">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={patientsByAge} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {patientsByAge.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Distribuição de Pacientes por Sexo" description="Gênero">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={patientsBySex} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                {patientsBySex.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Acessos por Área da Saúde" description="Atividade dos profissionais por especialidade">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={accessByArea} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="area" type="category" width={120} />
              <Tooltip />
              <Bar dataKey="total" fill="#06b6d4" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <Card>
          <CardHeader>
            <CardTitle>Tipos de Conteúdo Mais Comuns</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {contentTypes.map((ct) => (
                <div key={ct.type} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-lg">
                  <span className="font-medium">{ct.type || "Não especificado"}</span>
                  <span className="text-muted-foreground">{ct.count} documentos</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="col-span-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <CardTitle>Últimos 20 Acessos</CardTitle>
          </div>
          <CardDescription>Atividade recente dos profissionais na plataforma</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Profissional</TableHead>
                  <TableHead>Área</TableHead>
                  <TableHead>Ação</TableHead>
                  <TableHead>Entidade</TableHead>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentAccesses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Nenhum acesso registrado
                    </TableCell>
                  </TableRow>
                ) : (
                  recentAccesses.map((access) => (
                    <TableRow key={access.id}>
                      <TableCell className="font-medium">{access.professional_name}</TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">{access.area_display}</span>
                      </TableCell>
                      <TableCell>{formatActionName(access.action)}</TableCell>
                      <TableCell className="text-muted-foreground">{access.entity}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDistanceToNow(new Date(access.created_at), { addSuffix: true, locale: ptBR })}
                      </TableCell>
                      <TableCell className="text-sm font-mono text-muted-foreground">
                        {access.ip || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
